import * as OpenCC from 'opencc-js';
import {
  KANGXI_DICT_BASE64,
  NUMEROLOGY_81_BOON,
  NUMEROLOGY_81_LUCK,
  NUMEROLOGY_81_MEANING,
  NUMEROLOGY_81_SCORE,
} from '../data/kangxiData';
import { lookupNameologyStroke } from '../data/nameologyStrokeMap';
import { NUMEROLOGY_81_FULL_TEXT, NUMEROLOGY_81_META } from '../data/numerology81Full';

const SIMPLE_TO_TRAD = OpenCC.Converter({ from: 'cn', to: 'hk' });

export type SancaiGender = 'male' | 'female';
export type GridKey = 'tiange' | 'renge' | 'dige' | 'biange' | 'waige' | 'zongge';
export type Wuxing = '金' | '木' | '水' | '火' | '土';
export type LuckLevel = '吉' | '凶' | '半吉半凶' | '平';
export type StageLevel = '大吉' | '吉' | '平' | '小凶' | '凶' | '参考';

export interface StrokeSlot {
  key: string;
  part: 'surname' | 'given';
  partLabel: '姓' | '名';
  index: number;
  char: string;
  normalizedChar: string;
  autoStroke: number;
  finalStroke: number;
  source: 'dict' | 'online' | 'unknown';
  charWuxing: Wuxing | '';
}

export interface GridResult {
  key: GridKey;
  label: string;
  rawValue: number;
  value81: number;
  wuxing: Wuxing;
  yinYang: '阳' | '阴';
  luckLevel: LuckLevel;
  luckTag81: string;
  score81: number;
  meaning81: string;
  fullText81: string;
  fullTitle81: string;
  symbol81: string;
  boon: string;
}

export interface FortuneStage {
  key:
    | 'xiantian'
    | 'houtian'
    | 'shaonian'
    | 'qingnian'
    | 'zhuangnian'
    | 'zhongnian'
    | 'laonian'
    | 'jiating';
  label: string;
  ageRange: string;
  factors: string;
  level: StageLevel;
  summary: string;
}

export interface DiseaseRisk {
  rule: string;
  organs: string;
  triggeredBy: string[];
}

export interface SancaiLiugeResult {
  fullName: string;
  surname: string;
  givenName: string;
  gender: SancaiGender;
  strokeSlots: StrokeSlot[];
  grids: Record<GridKey, GridResult>;
  threeTalents: {
    sequence: string;
    relationTR: string;
    relationRD: string;
    summary: string;
  };
  stages: FortuneStage[];
  diseaseRisks: DiseaseRisk[];
  warnings: string[];
}

const WX_BY_DICT_INDEX: Wuxing[] = ['金', '木', '水', '火', '土'];

const NUM_ELEMENT_MAP: Record<number, Wuxing> = {
  0: '水',
  1: '木',
  2: '木',
  3: '火',
  4: '火',
  5: '土',
  6: '土',
  7: '金',
  8: '金',
  9: '水',
};

const LUCK_GOOD = new Set([
  1, 3, 5, 6, 7, 8, 11, 13, 15, 16, 17, 18, 21, 23, 24, 25, 29, 31, 32, 33, 35, 37, 39, 41, 45,
  47, 48, 52, 57, 63, 65, 68, 81,
]);

const LUCK_BAD = new Set([
  2, 4, 9, 10, 12, 14, 19, 20, 22, 26, 28, 34, 36, 40, 42, 43, 44, 46, 49, 50, 54, 56, 58, 59, 60, 62,
  64, 66, 69, 70, 71, 72, 74, 76, 79, 80,
]);

const LUCK_HALF = new Set([19, 22, 27, 30, 36, 38, 40, 49, 50, 51, 53, 55, 58, 73, 75, 77, 78]);

const KANGXI_STROKE_OVERRIDES: Record<string, number> = {
  罗: 19,
  羅: 19,
  诸: 15,
  諸: 15,
  淑: 12,
};

const FORCE_ORIGINAL_CHAR: Record<string, string> = {
  燕: '燕',
};

const GENERATE: Record<Wuxing, Wuxing> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

const CONTROL: Record<Wuxing, Wuxing> = {
  木: '土',
  土: '水',
  水: '火',
  火: '金',
  金: '木',
};

const DISEASE_RULES: Array<{ a: Wuxing; b: Wuxing; rule: string; organs: string }> = [
  { a: '木', b: '金', rule: '木与金相克', organs: '肝、胆、头、肩相关部位' },
  { a: '火', b: '水', rule: '火与水相克', organs: '心脏、小肠、额、齿相关部位' },
  { a: '土', b: '木', rule: '土与木相克', organs: '脾、胃、鼻、面相关部位' },
  { a: '金', b: '火', rule: '金与火相克', organs: '胸、肺、大肠、筋相关部位' },
  { a: '水', b: '土', rule: '水与土相克', organs: '肾、足、膀胱、颈相关部位' },
];

let KANGXI_DICT_BYTES: Uint8Array | null = null;

const decodeBase64 = (input: string): Uint8Array => {
  const binary = typeof atob === 'function' ? atob(input) : Buffer.from(input, 'base64').toString('binary');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const getKangxiDict = (): Uint8Array => {
  if (KANGXI_DICT_BYTES) return KANGXI_DICT_BYTES;
  KANGXI_DICT_BYTES = decodeBase64(KANGXI_DICT_BASE64);
  return KANGXI_DICT_BYTES;
};

const splitName = (value: string): string[] => [...value.trim()];

const isChineseChar = (char: string): boolean => {
  const cp = char.codePointAt(0);
  if (!cp) return false;
  if (cp >= 0x3400 && cp <= 0x4dbf) return true; // 扩展 A
  if (cp >= 0x4e00 && cp <= 0x9fff) return true; // 基本区
  if (cp >= 0xf900 && cp <= 0xfaff) return true; // 兼容区
  if (cp >= 0x20000 && cp <= 0x323af) return true; // 扩展 B-H
  return false;
};

const normalizeNameChar = (char: string): string => {
  if (FORCE_ORIGINAL_CHAR[char]) return FORCE_ORIGINAL_CHAR[char];
  const converted = SIMPLE_TO_TRAD(char);
  if (!converted || converted.length !== 1) return char;
  return converted;
};

const lookupDictByChar = (char: string): { stroke: number; wuxing: Wuxing | '' } => {
  const cp = char.codePointAt(0);
  if (!cp || cp < 0x4e00 || cp > 0x9fa5) return { stroke: 0, wuxing: '' };
  const bytes = getKangxiDict();
  const idx = (cp - 0x4e00) * 2;
  if (idx < 0 || idx + 1 >= bytes.length) return { stroke: 0, wuxing: '' };
  const stroke = bytes[idx] || 0;
  const wxIdx = bytes[idx + 1] || 0;
  return {
    stroke,
    wuxing: wxIdx > 0 ? WX_BY_DICT_INDEX[wxIdx - 1] : '',
  };
};

const resolveStrokeByChar = (char: string): { normalizedChar: string; stroke: number; wuxing: Wuxing | '' } => {
  if (!isChineseChar(char)) {
    return { normalizedChar: char, stroke: 0, wuxing: '' };
  }

  const strokeInNameology = lookupNameologyStroke(char);
  if (strokeInNameology > 0) {
    return {
      normalizedChar: char,
      stroke: strokeInNameology,
      wuxing: getWuxingByNumber(normalize81(strokeInNameology)),
    };
  }

  if (KANGXI_STROKE_OVERRIDES[char]) {
    return { normalizedChar: char, stroke: KANGXI_STROKE_OVERRIDES[char], wuxing: '' };
  }

  const normalizedChar = normalizeNameChar(char);
  const normalizedNameologyStroke = lookupNameologyStroke(normalizedChar);
  if (normalizedNameologyStroke > 0) {
    return {
      normalizedChar,
      stroke: normalizedNameologyStroke,
      wuxing: getWuxingByNumber(normalize81(normalizedNameologyStroke)),
    };
  }

  if (KANGXI_STROKE_OVERRIDES[normalizedChar]) {
    return { normalizedChar, stroke: KANGXI_STROKE_OVERRIDES[normalizedChar], wuxing: '' };
  }

  const fromNormalized = lookupDictByChar(normalizedChar);
  if (fromNormalized.stroke > 0) {
    return { normalizedChar, stroke: fromNormalized.stroke, wuxing: fromNormalized.wuxing };
  }

  const fallback = lookupDictByChar(char);
  return { normalizedChar: char, stroke: fallback.stroke, wuxing: fallback.wuxing };
};

const normalize81 = (value: number): number => {
  if (value <= 81) return value;
  return ((value - 81) % 80) + 1;
};

const getLuckLevel = (value81: number): LuckLevel => {
  const symbol = NUMEROLOGY_81_META[value81]?.symbol;
  if (symbol === '●') return '半吉半凶';
  if (symbol === '○' || symbol === '◎') return '吉';
  if (symbol === '△' || symbol === '▲') return '凶';
  if (LUCK_HALF.has(value81)) return '半吉半凶';
  if (LUCK_GOOD.has(value81)) return '吉';
  if (LUCK_BAD.has(value81)) return '凶';
  return '平';
};

const getWuxingByNumber = (value81: number): Wuxing => {
  const tail = value81 % 10;
  return NUM_ELEMENT_MAP[tail];
};

const getLuckScore = (level: LuckLevel): number => {
  if (level === '吉') return 2;
  if (level === '半吉半凶') return 0;
  if (level === '凶') return -2;
  return -1;
};

const toStageLevel = (score: number): StageLevel => {
  if (score >= 3) return '大吉';
  if (score >= 1.5) return '吉';
  if (score >= 0) return '平';
  if (score >= -1.5) return '小凶';
  return '凶';
};

const describeRelation = (a: Wuxing, b: Wuxing): { text: string; score: number } => {
  if (a === b) return { text: '比和', score: 0.5 };
  if (GENERATE[a] === b) return { text: `${a}生${b}`, score: 1 };
  if (GENERATE[b] === a) return { text: `${a}受${b}生`, score: 0.5 };
  if (CONTROL[a] === b) return { text: `${a}克${b}`, score: -1 };
  if (CONTROL[b] === a) return { text: `${a}受${b}克`, score: -1.5 };
  return { text: `${a}与${b}关系平`, score: 0 };
};

const createGridResult = (key: GridKey, label: string, rawValue: number): GridResult => {
  const value81 = normalize81(rawValue);
  const luckLevel = getLuckLevel(value81);
  const idx = value81 - 1;
  const fullText81 = NUMEROLOGY_81_FULL_TEXT[value81] || '';
  const fullTitle81 = fullText81.split('\n')[0]?.trim() || '';
  const symbol81 = NUMEROLOGY_81_META[value81]?.symbol || '';
  const luckTag81 = NUMEROLOGY_81_LUCK[idx] || '';
  const score81 = NUMEROLOGY_81_SCORE[idx] || 0;
  const meaning81 = NUMEROLOGY_81_MEANING[idx] || '';
  const boon = NUMEROLOGY_81_BOON[value81] || '';

  return {
    key,
    label,
    rawValue,
    value81,
    wuxing: getWuxingByNumber(value81),
    yinYang: value81 % 2 === 0 ? '阴' : '阳',
    luckLevel,
    luckTag81,
    score81,
    meaning81,
    fullText81,
    fullTitle81,
    symbol81,
    boon,
  };
};

const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);

const buildPairStage = (
  key: FortuneStage['key'],
  label: string,
  ageRange: string,
  factors: string,
  first: GridResult,
  second: GridResult,
): FortuneStage => {
  const relation = describeRelation(first.wuxing, second.wuxing);
  const score = getLuckScore(first.luckLevel) + getLuckScore(second.luckLevel) + relation.score;
  const level = toStageLevel(score);
  return {
    key,
    label,
    ageRange,
    factors,
    level,
    summary: `${factors}呈${relation.text}，综合偏${level}。`,
  };
};

const buildSingleStage = (
  key: FortuneStage['key'],
  label: string,
  ageRange: string,
  factors: string,
  target: GridResult,
): FortuneStage => {
  const level = toStageLevel(getLuckScore(target.luckLevel));
  return {
    key,
    label,
    ageRange,
    factors,
    level,
    summary: `${target.label}${target.value81}数（${target.wuxing}${target.yinYang}）主${level}。`,
  };
};

const buildDiseaseRisks = (renge: GridResult, compareGrids: GridResult[]): DiseaseRisk[] => {
  const riskMap = new Map<string, DiseaseRisk>();

  compareGrids.forEach((grid) => {
    const rule = DISEASE_RULES.find((item) => {
      const direct = item.a === renge.wuxing && item.b === grid.wuxing;
      const reverse = item.b === renge.wuxing && item.a === grid.wuxing;
      return direct || reverse;
    });

    if (!rule) return;
    const old = riskMap.get(rule.rule);
    if (!old) {
      riskMap.set(rule.rule, {
        rule: `${rule.rule}（人格${renge.wuxing} vs ${grid.label}${grid.wuxing}）`,
        organs: rule.organs,
        triggeredBy: [grid.label],
      });
      return;
    }
    old.triggeredBy.push(grid.label);
  });

  return [...riskMap.values()];
};

const validateName = (surname: string, givenName: string): string[] => {
  const errors: string[] = [];
  const s = splitName(surname);
  const g = splitName(givenName);

  if (s.length < 1 || s.length > 2) {
    errors.push('姓氏需为 1-2 个汉字。');
  }

  if (g.length < 1 || g.length > 2) {
    errors.push('名字需为 1-2 个汉字。');
  }

  if ([...s, ...g].some((char) => !isChineseChar(char))) {
    errors.push('仅支持中文姓名字符。');
  }

  return errors;
};

export const buildStrokeSlots = (
  surname: string,
  givenName: string,
  onlineStrokes: Record<string, number> = {},
): StrokeSlot[] => {
  const slots: StrokeSlot[] = [];
  const surnameChars = splitName(surname);
  const givenChars = splitName(givenName);

  surnameChars.forEach((char, index) => {
    const key = `s-${index}`;
    const resolved = resolveStrokeByChar(char);
    const online =
      (Number.isFinite(onlineStrokes[char]) && onlineStrokes[char] > 0 ? onlineStrokes[char] : 0)
      || (Number.isFinite(onlineStrokes[resolved.normalizedChar]) && onlineStrokes[resolved.normalizedChar] > 0
        ? onlineStrokes[resolved.normalizedChar]
        : 0);
    const finalStroke = online > 0 ? online : resolved.stroke;
    // 当本地库无此字时 resolved.wuxing 为空，需根据笔画数推导五行（含联网补齐的情况）
    const charWuxing: Wuxing | '' =
      resolved.wuxing || (finalStroke > 0 ? getWuxingByNumber(normalize81(finalStroke)) : '');

    slots.push({
      key,
      part: 'surname',
      partLabel: '姓',
      index,
      char,
      normalizedChar: resolved.normalizedChar,
      autoStroke: resolved.stroke,
      finalStroke,
      source: online > 0 ? 'online' : resolved.stroke > 0 ? 'dict' : 'unknown',
      charWuxing,
    });
  });

  givenChars.forEach((char, index) => {
    const key = `g-${index}`;
    const resolved = resolveStrokeByChar(char);
    const online =
      (Number.isFinite(onlineStrokes[char]) && onlineStrokes[char] > 0 ? onlineStrokes[char] : 0)
      || (Number.isFinite(onlineStrokes[resolved.normalizedChar]) && onlineStrokes[resolved.normalizedChar] > 0
        ? onlineStrokes[resolved.normalizedChar]
        : 0);
    const finalStroke = online > 0 ? online : resolved.stroke;
    const charWuxing: Wuxing | '' =
      resolved.wuxing || (finalStroke > 0 ? getWuxingByNumber(normalize81(finalStroke)) : '');

    slots.push({
      key,
      part: 'given',
      partLabel: '名',
      index,
      char,
      normalizedChar: resolved.normalizedChar,
      autoStroke: resolved.stroke,
      finalStroke,
      source: online > 0 ? 'online' : resolved.stroke > 0 ? 'dict' : 'unknown',
      charWuxing,
    });
  });

  return slots;
};

export const calculateSancaiLiuge = (params: {
  surname: string;
  givenName: string;
  gender: SancaiGender;
  onlineStrokes?: Record<string, number>;
}): { result: SancaiLiugeResult | null; errors: string[] } => {
  const surname = params.surname.trim();
  const givenName = params.givenName.trim();
  const errors = validateName(surname, givenName);

  const slots = buildStrokeSlots(surname, givenName, params.onlineStrokes || {});

  const missing = slots.filter((item) => !Number.isFinite(item.finalStroke) || item.finalStroke <= 0);
  if (missing.length > 0) {
    errors.push(`以下字符暂未查到有效姓名学笔画：${missing.map((item) => item.char).join('、')}。请检查网络后重试。`);
  }

  if (errors.length > 0) {
    return { result: null, errors };
  }

  const surnameSlots = slots.filter((item) => item.part === 'surname').sort((a, b) => a.index - b.index);
  const givenSlots = slots.filter((item) => item.part === 'given').sort((a, b) => a.index - b.index);

  const surnameStrokes = surnameSlots.map((item) => item.finalStroke);
  const givenStrokes = givenSlots.map((item) => item.finalStroke);

  const surnameCount = surnameStrokes.length;
  const givenCount = givenStrokes.length;

  const surnameFirst = surnameStrokes[0];
  const surnameSecond = surnameCount > 1 ? surnameStrokes[1] : surnameStrokes[0];
  const givenFirst = givenStrokes[0];
  const givenLast = givenStrokes[givenStrokes.length - 1];

  const tiange = createGridResult('tiange', '天格', surnameCount === 1 ? surnameFirst + 1 : sum(surnameStrokes));
  const renge = createGridResult('renge', '人格', (surnameCount === 1 ? surnameFirst : surnameSecond) + givenFirst);
  const dige = createGridResult('dige', '地格', givenCount === 1 ? givenFirst + 1 : sum(givenStrokes));
  const biange = createGridResult('biange', '变格', (surnameCount === 1 ? surnameFirst : surnameSecond) + givenLast);

  const waigeRaw =
    surnameCount === 1 && givenCount === 1
      ? 2
      : surnameCount === 1
        ? 1 + givenLast
        : givenCount === 1
          ? surnameFirst + 1
          : surnameFirst + givenLast;

  const waige = createGridResult('waige', '外格', waigeRaw);
  const zongge = createGridResult('zongge', '总格', sum(surnameStrokes) + sum(givenStrokes));

  const grids: Record<GridKey, GridResult> = {
    tiange,
    renge,
    dige,
    biange,
    waige,
    zongge,
  };

  const relationTR = describeRelation(tiange.wuxing, renge.wuxing);
  const relationRD = describeRelation(renge.wuxing, dige.wuxing);

  const stages: FortuneStage[] = [
    buildSingleStage('xiantian', '先天主运', '先天', '人格数理', renge),
    buildSingleStage('houtian', '后天主运', '后天', '变格数理', biange),
    buildPairStage('shaonian', '少年运', '1-15岁', '天格+地格', tiange, dige),
    buildPairStage('qingnian', '青年运', '12-27岁', '人格+地格', renge, dige),
    buildPairStage('zhuangnian', '壮年运', '24-39岁', '天格+人格', tiange, renge),
    buildPairStage('zhongnian', '中年运', '36-51岁', '人格+外格', renge, waige),
    buildSingleStage('laonian', '老年运', '48-63岁', '总格数理', zongge),
    params.gender === 'female'
      ? buildSingleStage('jiating', '家庭运', '已婚女性', '女性总格数理', zongge)
      : {
          key: 'jiating',
          label: '家庭运',
          ageRange: '已婚女性',
          factors: '女性总格数理',
          level: '参考',
          summary: '该项在原规则中以女性已婚家庭运为主，男性可作参考。',
        },
  ];

  const diseaseRisks = buildDiseaseRisks(renge, [tiange, dige, waige, biange]);

  const threeTalentsSummary = (() => {
    const score = relationTR.score + relationRD.score;
    if (score >= 1.5) return '三才偏相生，整体气势较顺。';
    if (score >= 0) return '三才有生有和，整体中性偏稳。';
    if (score >= -1) return '三才中存在克制，阶段起伏会偏明显。';
    return '三才克制偏重，建议结合后天努力与取名校正综合判断。';
  })();

  const warnings = [
    '结果为姓名数理模型推演，仅供文化研究与娱乐参考。',
  ];

  return {
    result: {
      fullName: `${surname}${givenName}`,
      surname,
      givenName,
      gender: params.gender,
      strokeSlots: slots,
      grids,
      threeTalents: {
        sequence: `${tiange.wuxing}-${renge.wuxing}-${dige.wuxing}`,
        relationTR: relationTR.text,
        relationRD: relationRD.text,
        summary: threeTalentsSummary,
      },
      stages,
      diseaseRisks,
      warnings,
    },
    errors: [],
  };
};
